import { useCallback, useMemo, useState } from 'react';
import { AccessibilityInfo, Platform, StyleSheet, Text, View } from 'react-native';

import { tokens } from '../../tokens';
import { XStack, type XStackProps } from '../stack';

import { useQuantity } from './context';
import { QuantityCounterBox, type QuantityCounterBoxProps } from './quantity-box';
import { QuantityStepperButton } from './quantity-stepper-button';

function buildStepperChangeAnnouncement(
  quantityLabel: string | undefined,
  newOptionLabel: string | null | undefined,
  nutritionOrPriceLine?: string | null
): string | null {
  if (newOptionLabel == null || String(newOptionLabel).trim() === '') {
    return null;
  }
  const trimmed = quantityLabel?.trim();
  const extra = nutritionOrPriceLine?.trim();
  const suffix = extra ? ` ${extra}` : '';
  if (!trimmed) {
    return `Now ${newOptionLabel}.${suffix}`.trim();
  }
  const itemName = trimmed.split('.')[0]?.trim() || trimmed;
  return `${itemName}, now ${newOptionLabel}.${suffix}`.trim();
}

type QuantityStepperProps = Partial<XStackProps> & {
  valueBoxProps?: QuantityCounterBoxProps;
  decrementDisabled?: boolean;
  incrementDisabled?: boolean;
  incrementSoftDisabled?: boolean;
  isLoading?: boolean;
  onIncrementSoftDisabledPress?: () => void;
};

export function QuantityStepper({
  valueBoxProps,
  decrementDisabled,
  incrementDisabled,
  incrementSoftDisabled,
  onIncrementSoftDisabledPress,
  isLoading = false,
  ...containerProps
}: QuantityStepperProps) {
  const {
    accessibilityHint,
    label,
    disabled,
    value,
    onChangeValue,
    options,
    decrementAccessibilityLabel = 'Decrease quantity',
    incrementAccessibilityLabel = 'Increase quantity',
  } = useQuantity();

  const useGroupedStepperSemantics = Platform.OS !== 'web';

  const selectedOptionsIndex = useMemo(
    () => options.findIndex(opt => opt.value === value) ?? 0,
    [options, value]
  );

  const selectedOption = (options[selectedOptionsIndex] || options[0]) ?? {
    label: null,
    value: null,
  };

  const isDecrementDisabled = disabled || decrementDisabled || selectedOptionsIndex <= 0;
  const isIncrementDisabled =
    disabled || incrementDisabled || selectedOptionsIndex >= options.length - 1;

  const [webAnnouncement, setWebAnnouncement] = useState('');

  const flushWebAnnouncement = useCallback((msg: string) => {
    setWebAnnouncement('');
    requestAnimationFrame(() => setWebAnnouncement(msg));
  }, []);

  const announceValueChange = useCallback(
    (
      newOptionLabel: string | null | undefined,
      accessibilityDescription?: string | null | undefined
    ) => {
      const msg = buildStepperChangeAnnouncement(label, newOptionLabel, accessibilityDescription);
      if (!msg) {
        return;
      }
      if (Platform.OS === 'web') {
        flushWebAnnouncement(msg);
      } else {
        AccessibilityInfo.announceForAccessibility(msg);
      }
    },
    [label, flushWebAnnouncement]
  );

  const handleIncrement = () => {
    if (!isIncrementDisabled && !incrementSoftDisabled && !isLoading) {
      const next = options[selectedOptionsIndex + 1];
      if (next) {
        onChangeValue(next.value);
        announceValueChange(next.label, next.accessibilityDescription);
      }
    }
  };

  const handleDecrement = () => {
    if (!isDecrementDisabled) {
      const next = options[selectedOptionsIndex - 1];
      if (next) {
        onChangeValue(next.value);
        announceValueChange(next.label, next.accessibilityDescription);
      }
    }
  };

  return (
    <XStack
      {...(useGroupedStepperSemantics
        ? {
            accessible: true,
            role: 'spinbutton' as const,
            'aria-disabled': disabled,
            'aria-valuetext': String(selectedOption.label ?? value),
            'aria-label': label,
            accessibilityHint,
            accessibilityActions: [
              { name: 'increment', label: 'increment' },
              { name: 'decrement', label: 'decrement' },
            ],
            onAccessibilityAction: event => {
              switch (event.nativeEvent.actionName) {
                case 'increment': {
                  handleIncrement();
                  break;
                }
                case 'decrement': {
                  handleDecrement();
                  break;
                }
              }
            },
          }
        : { accessible: false })}
      {...containerProps}
      style={[styles.container, containerProps.style]}
    >
      {Platform.OS === 'web' ? (
        <View
          pointerEvents="none"
          style={styles.a11yLiveRegion}
          accessibilityLiveRegion="polite"
        >
          <Text style={styles.a11yLiveRegionText}>{webAnnouncement}</Text>
        </View>
      ) : null}
      <QuantityStepperButton
        disabled={isDecrementDisabled}
        iconName="REMOVE"
        accessibilityLabel={decrementAccessibilityLabel}
        handlePress={() => {
          handleDecrement();
        }}
        testID="decrement-button-for-number-input"
      />

      {isLoading ? (
        <XStack style={styles.quantitySkeleton} />
      ) : (
        <QuantityCounterBox value={selectedOption.label} {...valueBoxProps} />
      )}

      <QuantityStepperButton
        disabled={isIncrementDisabled}
        softDisabled={incrementSoftDisabled}
        iconName="ADD"
        accessibilityLabel={incrementAccessibilityLabel}
        handleSoftDisabledPress={onIncrementSoftDisabledPress}
        handlePress={() => {
          handleIncrement();
        }}
        testID="increment-button-for-number-input"
      />
    </XStack>
  );
}

const styles = StyleSheet.create({
  a11yLiveRegion: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 1,
    height: 1,
    opacity: 0,
    overflow: 'hidden',
    zIndex: -1,
  },
  a11yLiveRegionText: {
    fontSize: 1,
  },
  container: {
    position: 'relative',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
  },
  incrementSoftDisabledMessage: {
    marginLeft: 5,
    color: 'white',
  },
  quantitySkeleton: {
    width: 50,
    height: 16,
    borderRadius: 12,
    marginHorizontal: 8,
    backgroundColor: tokens.colors.$houseLight,
  },
});
